import { StandardEvents } from '@shopify/events';

/**
 * Keeps "Specificații complete" on the selected variant
 * (`blocks/product-spec-table.liquid`, OI-134).
 *
 * The card is rendered on the server from the selected variant's
 * `custom.descriere_tehnica`. On the product page Horizon's variant picker
 * already fetches the whole page for the new variant and hands it to every
 * listener through the `shopify:product:select` promise, so this element
 * finds itself in that page by block id and takes its content. No request of
 * its own.
 *
 * The picker lives in another section, so the element listens on the
 * document. A selection made in a product card or a quick-add dialog is
 * another product, or a page fragment without this block, and is ignored. So
 * is an option combination that matches no variant: the server answers it
 * with the first available variant, whose rows would be the wrong ones.
 */
class AdmiraSpecTable extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    this.#controller = new AbortController();
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal: this.#controller.signal,
    });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /**
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (event.target instanceof Element && event.target.closest('product-card, quick-add-dialog, dialog')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html || !detail.resource) return;
        if (detail.productId && detail.productId !== this.dataset.productId) return;

        const next = detail.html.querySelector(`admira-spec-table[data-block-id="${this.dataset.blockId}"]`);
        if (!(next instanceof HTMLElement)) return;

        // Copied rather than moved: other listeners read the same document.
        this.innerHTML = next.innerHTML;
        this.hidden = next.hidden;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[admira-spec-table] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('admira-spec-table')) {
  customElements.define('admira-spec-table', AdmiraSpecTable);
}
